import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiLogOut, FiUser, FiLogIn } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, confirmAction, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';

const emptyForm = { name: '', email: '', password: '', roles: [], is_active: true };

export default function Users() {
  const { hasPermission, user: currentUser, impersonate } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', per_page: 100 });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.users.list(params);
      setRows(Array.isArray(data.data) ? data.data : data.users?.data || data.users || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load users', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.roles.list({ per_page: 100 }).then(({ data }) => {
      const list = Array.isArray(data) ? data : data.roles ? (data.roles.data || data.roles) : data.data || [];
      setRoles(list);
    }).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      email: row.email,
      password: '',
      roles: (row.roles || []).map((r) => (typeof r === 'object' ? r.id : r)),
      is_active: row.is_active !== false,
    });
    setModal(true);
  };

  const toggleRole = (id) => {
    setForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(id) ? prev.roles.filter((r) => r !== id) : [...prev.roles, id],
    }));
  };

  const isAdminUser = (row) => (row.roles || []).some((role) => (typeof role === 'object' ? String(role.name).toLowerCase() === 'admin' : false));
  const editingIsAdmin = editing && isAdminUser(editing);

  const handleSave = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Name is required';
    if (!form.email) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email address';
    if (!editing && !form.password) errs.password = 'Password is required for new users';
    else if (form.password && form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (!form.roles.length) errs.roles = 'Select at least one role';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, password: form.password || undefined };
      if (editing) {
        await api.users.update(editing.id, payload);
        toast('success', '', 'User updated successfully');
      } else {
        await api.users.create(payload);
        toast('success', '', 'User created successfully');
      }
      setModal(false);
      load();
    } catch (err) {
      showError('Failed to save user', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete(`user "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.users.remove(row.id);
      toast('success', '', 'User deleted');
      load();
    } catch (err) {
      showError('Failed to delete user', extractError(err));
    }
  };

  const handleForceLogout = async (row) => {
    const res = await confirmAction(
      'Force Logout?',
      `${row.name} will be immediately signed out from all devices.`,
      'Yes, logout!'
    );
    if (!res.isConfirmed) return;
    setActionLoading(row.id);
    try {
      const { data } = await api.users.forceLogout(row.id);
      toast('success', '', data.message);
    } catch (err) {
      showError('Failed to force logout', extractError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (row) => {
    const res = await confirmAction(
      'Login as user?',
      `You will see the system as ${row.name}. You can return anytime.`,
      'Yes, login!'
    );
    if (!res.isConfirmed) return;
    setActionLoading(row.id);
    try {
      await impersonate(row.id);
      toast('info', '', `You are now logged in as ${row.name}. Use the "Back to Admin" banner to return.`);
    } catch (err) {
      showError('Failed to login as user', extractError(err));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Users"
        subtitle="Manage system users and their role assignments."
        crumbs={[{ label: 'Administration' }, { label: 'Users' }]}
        actions={hasPermission('create-users') && (
          <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Add User</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input
                placeholder="Search users..."
                value={params.search}
                onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })}
              />
            </div>
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="users" label="users" />

          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No users found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Name', render: (r) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 13, borderRadius: 8 }}>
                    {r.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.email}</div>
                  </div>
                </div>
              ) },
              { key: 'roles', label: 'Roles', render: (r) => (
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {(r.roles || []).map((role, i) => (
                    <span key={i} className="badge badge-indigo">{typeof role === 'object' ? role.name : role}</span>
                  ))}
                </div>
              ) },
              { key: 'is_active', label: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'inactive'} /> },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('update-users') && (
                    <button className="btn-icon" title="Edit" onClick={() => openEdit(r)}><FiEdit2 /></button>
                  )}
                  {hasPermission('update-users') && r.id !== currentUser?.id && (
                    <button
                      className="btn-icon"
                      title={`Force logout ${r.name}`}
                      disabled={actionLoading === r.id}
                      onClick={() => handleForceLogout(r)}
                      style={{ color: '#e67e22' }}
                    ><FiLogOut /></button>
                  )}
                  {hasPermission('update-users') && r.id !== currentUser?.id && (
                    <button
                      className="btn-icon"
                      title={`Login as ${r.name}`}
                      disabled={actionLoading === r.id}
                      onClick={() => handleImpersonate(r)}
                      style={{ color: '#27ae60' }}
                    ><FiLogIn /></button>
                  )}
                  {hasPermission('delete-users') && !isAdminUser(r) && (
                    <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                  )}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editing ? 'Edit User' : 'Add User'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update User' : 'Create User'}
            </button>
          </>
        }
      >
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Name <span className="req">*</span></label>
            <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} placeholder="Full name" />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Email <span className="req">*</span></label>
            <input className={`form-control ${errors.email ? 'is-invalid' : ''}`} type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors((p) => ({ ...p, email: '' })); }} placeholder="email@company.com" />
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">{editing ? 'New Password (leave blank to keep)' : 'Password'} {!editing && <span className="req">*</span>}</label>
          <input className={`form-control ${errors.password ? 'is-invalid' : ''}`} type="password" value={form.password} onChange={(e) => { setForm({ ...form, password: e.target.value }); if (errors.password) setErrors((p) => ({ ...p, password: '' })); }} placeholder="Min 8 characters" />
          {errors.password && <div className="invalid-feedback">{errors.password}</div>}
        </div>
        {editingIsAdmin && (
          <div className="alert-banner warn" style={{ marginBottom: 14 }}>
            Admin account is protected — role assignment and active status cannot be changed.
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Roles</label>
          <div className="grid-2">
            {roles.map((role) => (
              <label key={role.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.roles.includes(role.id)}
                  disabled={editingIsAdmin && String(role.name).toLowerCase() === 'admin'}
                  onChange={() => toggleRole(role.id)}
                />
                {role.name}
              </label>
            ))}
          </div>
          {errors.roles && <div className="invalid-feedback">{errors.roles}</div>}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={form.is_active}
            disabled={editingIsAdmin}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          Active user
        </label>
      </Modal>
    </div>
  );
}
